import { X, ExternalLink } from '@/utils/icons';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ProfileBadge } from '@/components/common/ProfileBadge';
import { ExportMenu } from '@/components/common/ExportMenu';
import { useNodes } from '@/hooks/useNodes';
import { Link } from 'react-router-dom';
import {
  createBrandedPdf,
  downloadPdf,
  addBrandedTable,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv, formatExportDate } from '@/utils/csv';
import { formatTimeAgo } from '@/utils/formatters';

interface NodeListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  filter: {
    softwareVersion?: string;
    vendor?: string;
    profile?: string;
    portfolio?: string;
  } | null;
}

export function NodeListDrawer({ isOpen, onClose, title, filter }: NodeListDrawerProps) {
  const { nodes, loading, total } = useNodes(isOpen && filter ? filter : undefined);

  const handleExportPdf = async () => {
    if (nodes.length === 0) return;

    try {
      const logoData = await loadLogoForPdf();
      const layout = createBrandedPdf({
        title: `Node List: ${title}`,
        subtitle: `${nodes.length} Nodes`,
        logoData,
      });

      addBrandedTable(
        layout.doc,
        ['Name', 'S/N', 'Status', 'Profile', 'Version', 'Portfolio', 'Last Contact'],
        nodes.map((n) => [
          n.name,
          n.hardwareSerialNumber || '-',
          n.currentNodeState,
          n.profileName || '-',
          n.softwareVersion || '-',
          n.portfolio || '-',
          n.lastContactedAt ? new Date(n.lastContactedAt).toLocaleString() : '-',
        ]),
        layout.startY,
        layout
      );

      downloadPdf(layout.doc, exportFilename('node-list', 'pdf'));
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  const handleExportCsv = () => {
    if (nodes.length === 0) return;

    downloadCsv(
      nodes.map((n) => ({
        Name: n.name,
        'S/N': n.hardwareSerialNumber || '',
        Status: n.currentNodeState,
        Profile: n.profileName || '',
        Version: n.softwareVersion || '',
        Portfolio: n.portfolio || '',
        'Last Contact': formatExportDate(n.lastContactedAt),
      })),
      exportFilename('node-list', 'csv'),
      { report: 'Node List' }
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="dls-fixed dls-inset-0 dls-bg-white/30 dls-backdrop-blur-sm dls-z-40" onClick={onClose} />
      <div className="dls-fixed dls-right-0 dls-top-0 dls-bottom-0 dls-w-full dls-max-w-2xl dls-bg-background dls-border-l dls-shadow-50 dls-z-50 dls-flex dls-flex-col">
        <div className="dls-flex dls-items-center dls-justify-between dls-px-6 dls-py-4 dls-border-b">
          <div>
            <h2 className="dls-text-lg dls-font-semibold">{title}</h2>
            <p className="dls-text-sm dls-text-muted-foreground">
              {loading ? 'Loading...' : `${total} Nodes found`}
            </p>
          </div>
          <div className="dls-flex dls-items-center dls-gap-2">
            {nodes.length > 0 && !loading && (
              <ExportMenu
                onExportPdf={handleExportPdf}
                onExportCsv={handleExportCsv}
                label="Export"
              />
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="dls-h-5 dls-w-5" />
            </Button>
          </div>
        </div>

        <div className="dls-flex-1 dls-overflow-y-auto dls-p-6">
          {loading ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-full">
              <div className="dls-animate-spin dls-rounded-full dls-h-8 dls-w-8 dls-border-b-2 dls-border-primary" />
            </div>
          ) : nodes.length === 0 ? (
            <div className="dls-flex dls-items-center dls-justify-center dls-h-full dls-text-muted-foreground">
              No nodes found matching criteria.
            </div>
          ) : (
            <div className="dls-space-y-4">
              {nodes.map((node) => (
                <div
                  key={node.id}
                  className="dls-flex dls-items-center dls-justify-between dls-p-4 dls-border dls-rounded-lg hover:dls-bg-muted/50 dls-transition-colors"
                >
                  <div className="dls-space-y-1">
                    <div className="dls-flex dls-items-center dls-gap-2">
                      <Link
                        to={`/nodes/${node.id}`}
                        className="dls-font-medium hover:dls-underline dls-flex dls-items-center dls-gap-1"
                      >
                        {node.name}
                        <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                      </Link>
                    </div>
                    <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap dls-text-xs dls-text-muted-foreground">
                      {node.hardwareSerialNumber && <span>S/N: {node.hardwareSerialNumber}</span>}
                      {node.hardwareSerialNumber && <span>•</span>}
                      <span>{node.softwareVersion || 'No version'}</span>
                      {node.portfolio && (
                        <>
                          <span>•</span>
                          <span>{node.portfolio}</span>
                        </>
                      )}
                      {node.lastContactedAt && (
                        <>
                          <span>•</span>
                          <span>Last contact: {formatTimeAgo(node.lastContactedAt)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="dls-flex dls-items-center dls-gap-2">
                    <ProfileBadge profileName={node.profileName} />
                    <StatusBadge state={node.currentNodeState} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
